import { createApi } from '@reduxjs/toolkit/query/react';
import { MFAPayloadRequest, MFAPayoloadResponse } from 'src/dto/User';
import { baseQuery } from './utils';

interface Status {
  status: 'ok' | 'challenge' | 'redirect_reset_password' | 'redirect_resend_confirmation';
}
export type LoginResultType = {
  token: string;
  challenge: string;
  session?: string;
} & Status;
interface CommonAuthRequestType {
  username: string;
  password: string;
  session_id?: string;
  phone_number?: string;
  phone_country_code?: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  company_size?: string;
  usage_reason?: string;
  device_key?: string;
  device_group_key?: string;
  device_password?: string;
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQuery,
  tagTypes: ['Auth'],
  endpoints: (builder) => ({
    loginUser: builder.mutation<LoginResultType, CommonAuthRequestType>({
      query(data) {
        return {
          url: '/v1/auth/sign-in',
          method: 'POST',
          body: data,
        };
      },
    }),
    signUpUser: builder.mutation<unknown, CommonAuthRequestType>({
      query(data) {
        return {
          url: '/v2/auth/signup',
          method: 'POST',
          body: data,
        };
      },
    }),
    forgotPassword: builder.mutation<Status & { info: 'resend' | 'forgot' }, { username: string }>({
      query(data) {
        return {
          url: '/v1/auth/forgot-password',
          method: 'POST',
          body: data,
        };
      },
    }),
    forgotPasswordConfirm: builder.mutation<Status, CommonAuthRequestType & { secret_hash: string }>({
      query(data) {
        return {
          url: '/v1/auth/forgot-password-confirm',
          method: 'POST',
          body: data,
        };
      },
    }),
    newPasswordChallenge: builder.mutation<LoginResultType, { username: string; new_password: string; old_password: string }>({
      query(data) {
        return {
          url: '/v1/auth/new-password-challenge',
          method: 'POST',
          body: data,
        };
      },
    }),
    resendActivation: builder.mutation<Status, { username: string }>({
      query(data) {
        return {
          url: '/v1/auth/resend-confirmation',
          method: 'POST',
          body: data,
        };
      },
    }),

    authMFA: builder.mutation<MFAPayoloadResponse, MFAPayloadRequest>({
      query(data) {
        return {
          url: '/v1/auth/mfa',
          method: 'POST',
          body: data,
        };
      },
    }),
  }),
});

export const {
  useLoginUserMutation,
  useSignUpUserMutation,
  useAuthMFAMutation,
  useForgotPasswordMutation,
  useForgotPasswordConfirmMutation,
  useNewPasswordChallengeMutation,
  useResendActivationMutation,
} = authApi;
